import { FormEvent, useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../api/client';
import { useAuth } from '../hooks/useAuth';
import { t } from '../hooks/useT';
import { isValidDisplayName, normalizeDisplayName } from '../utils/displayName';

const DISPLAY_NAME_DRAFT_KEY = 'dezoom_display_name_draft';

const AUTH_ERRORS: Record<string, string> = {
  display_name_required: 'profile.nameRequired',
  display_name_invalid: 'profile.nameInvalid',
  display_name_taken: 'profile.nameTaken',
};

function GoogleIcon() {
  return (
    <svg className="btn-icon" width="20" height="20" viewBox="0 0 24 24" aria-hidden="true">
      <path
        fill="#4285F4"
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
      />
      <path
        fill="#34A853"
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
      />
      <path
        fill="#FBBC05"
        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
      />
      <path
        fill="#EA4335"
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
      />
    </svg>
  );
}

function readDisplayNameDraft(): string {
  try {
    return localStorage.getItem(DISPLAY_NAME_DRAFT_KEY) || '';
  } catch {
    return '';
  }
}

function saveDisplayNameDraft(value: string) {
  try {
    localStorage.setItem(DISPLAY_NAME_DRAFT_KEY, value);
  } catch {
    /* ignore */
  }
}

export function LoginPage() {
  const { requestMagicLink, loginGuest } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const authErrorCode = searchParams.get('authError');
  const authError = authErrorCode && AUTH_ERRORS[authErrorCode]
    ? t(AUTH_ERRORS[authErrorCode])
    : '';
  const [displayName, setDisplayName] = useState(readDisplayNameDraft);
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [googleEnabled, setGoogleEnabled] = useState(false);

  useEffect(() => {
    api<{ magicLink: boolean; google: boolean }>('/auth/providers')
      .then((p) => setGoogleEnabled(p.google))
      .catch(() => setGoogleEnabled(false));
  }, []);

  const validateDisplayName = (): string | null => {
    const normalized = normalizeDisplayName(displayName);
    if (!normalized) {
      setError(t('profile.nameRequired'));
      return null;
    }
    if (!isValidDisplayName(normalized)) {
      setError(t('profile.nameInvalid'));
      return null;
    }
    return normalized;
  };

  const handleMagic = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    const normalizedName = validateDisplayName();
    if (!normalizedName) return;

    setBusy(true);
    try {
      saveDisplayNameDraft(normalizedName);
      await requestMagicLink(email, normalizedName);
      setSent(true);
    } catch (err: unknown) {
      const code = (err as { data?: { error?: string } }).data?.error;
      const key = code && AUTH_ERRORS[code];
      setError(key ? t(key) : t('auth.error'));
    } finally {
      setBusy(false);
    }
  };

  const handleGoogle = () => {
    setError('');
    const normalizedName = validateDisplayName();
    if (!normalizedName) return;
    saveDisplayNameDraft(normalizedName);
    window.location.href = `/api/auth/google?displayName=${encodeURIComponent(normalizedName)}`;
  };

  const handleGuest = async () => {
    setBusy(true);
    try {
      await loginGuest();
      navigate('/play');
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="page login">
      <div className="login-card">
        <p className="login-brand">Dezoom</p>
        <h1>{t('auth.title')}</h1>
        <p className="login-sub">{t('auth.subtitle')}</p>

        {authError && <p className="error login-error" role="alert">{authError}</p>}

        {sent ? (
          <div className="login-sent" role="status">
            <span className="login-sent-icon" aria-hidden="true">✉</span>
            <p>{t('auth.sent')}</p>
            <p className="login-sent-hint">{email}</p>
            <p className="login-sent-note">{t('auth.sentHint')}</p>
            <button
              type="button"
              className="btn secondary block"
              onClick={() => { setSent(false); setEmail(''); }}
            >
              {t('auth.changeEmail')}
            </button>
          </div>
        ) : (
          <>
            <form onSubmit={handleMagic} className="login-form">
              <label htmlFor="displayName">{t('auth.displayName')}</label>
              <input
                id="displayName"
                type="text"
                required
                autoComplete="nickname"
                maxLength={32}
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder={t('auth.displayNamePlaceholder')}
                disabled={busy}
              />
              <p className="login-field-hint">{t('auth.displayNameHint')}</p>

              <label htmlFor="email">{t('auth.email')}</label>
              <input
                id="email"
                type="email"
                required
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="toi@exemple.fr"
                disabled={busy}
              />
              <button type="submit" className="btn primary block" disabled={busy}>
                {busy ? t('auth.sending') : t('auth.magicLink')}
              </button>
            </form>

            {error && <p className="error login-error" role="alert">{error}</p>}

            {googleEnabled && (
              <>
                <div className="login-divider" aria-hidden="true">
                  <span>{t('auth.or')}</span>
                </div>
                <button
                  type="button"
                  className="btn btn-google block"
                  onClick={handleGoogle}
                  disabled={busy}
                >
                  <GoogleIcon />
                  {t('auth.google')}
                </button>
              </>
            )}
          </>
        )}

        <div className="login-footer">
          <button
            type="button"
            className="btn ghost block"
            onClick={handleGuest}
            disabled={busy}
          >
            {t('landing.ctaGuest')}
          </button>
          <Link to="/" className="link-muted login-back">{t('auth.back')}</Link>
        </div>
      </div>
    </main>
  );
}
